import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import "@/app/App.scss";
import { AppProviders } from "./providers/AppProviders";
import { CityProvider, useCity } from "@/app/providers/CityProvider";
import { CatalogModule } from "@/modules/catalog";
import { CartModule } from "@/modules/cart";
import { OrdersModule } from "@/modules/orders";
import { BottomCartBar } from "@/shared/components/BottomCartBar";
import { Header } from "@/shared/components/Header";
import { PendingPaymentBanner } from "@/shared/components/PendingPaymentBanner";
import { usePendingOrderSync } from "@/shared/hooks/usePendingOrderSync";
import { consumePostPaymentOrderId } from "@/shared/utils/postPaymentNavigation";
import { useCart } from "./providers/CartProvider";

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { totalAmount, totalQuantity, bindCartScope } = useCart();
  const { selectedCity, isLoading: isCityLoading } = useCity();

  useEffect(() => {
    if (selectedCity?.id) {
      bindCartScope(selectedCity.id, selectedCity.outletId ?? null);
    }
  }, [selectedCity?.id, selectedCity?.outletId, bindCartScope]);

  useEffect(() => {
    const orderIdFromUrl = consumePostPaymentOrderId();
    if (!orderIdFromUrl) return;
    navigate(`/orders?order_id=${encodeURIComponent(orderIdFromUrl)}`, {
      replace: true,
    });
  }, [navigate]);

  const { showUnpaidReminder } = usePendingOrderSync();

  const currentScreen = location.pathname.replace("/", "") || "catalog";

  const handleNavigate = (screen: string) => {
    navigate(`/${screen === "catalog" ? "" : screen}`);
  };

  const handleCheckout = () => {
    // handleNavigate("catalog");
  };

  if (isCityLoading) {
    return (
      <div className="app__loader">
        <div className="spinner"></div>
        <span>Загрузка...</span>
      </div>
    );
  }

  return (
    <div className="app">
      <Header
        onBack={() => handleNavigate("catalog")}
        showBack={currentScreen !== "catalog"}
        onOrdersClick={() => handleNavigate("orders")}
        showOrdersButton={currentScreen !== "orders"}
      />

      <PendingPaymentBanner visible={showUnpaidReminder} />

      <main className="app__content">
        <Routes>
          <Route path="/" element={<CatalogModule key={selectedCity?.id} />} />
          <Route
            path="/cart"
            element={
              <CartModule
                onClose={() => handleNavigate("catalog")}
                onCheckout={handleCheckout}
              />
            }
          />
          <Route
            path="/orders"
            element={<OrdersModule onClose={() => handleNavigate("catalog")} />}
          />
        </Routes>
      </main>

      {currentScreen === "catalog" && totalQuantity > 0 && (
        <BottomCartBar
          key={`cart-${totalQuantity}-${totalAmount}`}
          itemsCount={totalQuantity}
          totalAmount={totalAmount}
          onCartClick={() => handleNavigate("cart")}
        />
      )}
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppProviders>
        <CityProvider>
          <AppContent />
        </CityProvider>
      </AppProviders>
    </BrowserRouter>
  );
};
